import { Checkbox, Divider, Radio, RadioGroup, Typography } from '@trackman/web-shared-components';

import { useTranslation } from 'react-i18next';

type SelectionType = 'Checkbox' | 'Radio';

type RichRadioOption<RadioValue extends string, SubOptionValue extends string> = {
  value: RadioValue;
  subOptions: {
    type: SelectionType;
    options: SubOptionValue[];
    selected: SubOptionValue[];
    setSelected: (selected: SubOptionValue[]) => void;
  };
};

type OptionType<R extends string = string, S extends string = string> = R | RichRadioOption<R, S>;

type Props<R extends string, S extends string> = {
  label: string;
  options: OptionType<R, S>[];
  selected: R;
  setSelected: (selected: R) => void;
  disabled: boolean;
  useShortLabels?: boolean;
};

const GenericRadioConfig = <R extends string, S extends string>({
  label,
  options,
  selected,
  setSelected,
  disabled,
  useShortLabels,
}: Props<R, S>) => {
  const { t } = useTranslation();

  const handleSubOptionToggle = (type: SelectionType, subOption: S, currentSelected: S[], setSelectedSub: (selected: S[]) => void) => {
    if (type === 'Radio') {
      setSelectedSub([subOption]);
    } else {
      if (currentSelected.includes(subOption)) {
        setSelectedSub(currentSelected.filter((item) => item !== subOption));
      } else {
        setSelectedSub([...currentSelected, subOption]);
      }
    }
  };

  return (
    <>
      <Typography variant='label bold' component='div' color='#A0A0A0'>
        {t(label)}
      </Typography>
      <RadioGroup
        style={{ gap: '16px' }}
        value={selected}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelected((event.target as HTMLInputElement).value as R)}
      >
        {options.map((option) => {
          const isSimple = typeof option === 'string';
          const radioValue = isSimple ? option : option.value;
          const isSelectedAndRich = !isSimple && option.value === selected;
          const subOptionsData = isSelectedAndRich ? option.subOptions : undefined;

          const label = t(radioValue);
          const shortLabel = useShortLabels && t('short.' + radioValue);

          return (
            <div key={radioValue} style={{ display: 'flex', flexDirection: 'column' }}>
              <Radio
                value={radioValue}
                label={useShortLabels ? shortLabel : label}
                hint={useShortLabels && label !== shortLabel && label}
                disabled={disabled}
              />
              {subOptionsData && (
                <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
                  {subOptionsData.type === 'Radio' ? (
                    <RadioGroup
                      value={subOptionsData.selected[0] ?? ''}
                      onChange={(e) =>
                        handleSubOptionToggle(
                          'Radio',
                          (e.target as HTMLInputElement).value as S,
                          subOptionsData.selected,
                          subOptionsData.setSelected
                        )
                      }
                    >
                      {subOptionsData.options.map((subOption) => (
                        <Radio key={subOption} value={subOption} label={t(subOption)} disabled={disabled} />
                      ))}
                    </RadioGroup>
                  ) : (
                    subOptionsData.options.map((subOption) => (
                      <Checkbox
                        key={subOption}
                        label={t(subOption)}
                        checked={subOptionsData.selected.includes(subOption)}
                        onChange={() => handleSubOptionToggle('Checkbox', subOption, subOptionsData.selected, subOptionsData.setSelected)}
                        disabled={disabled}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </RadioGroup>
      <Divider />
    </>
  );
};
export default GenericRadioConfig;
